// Waitlist Page - Email capture
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle2, Mail, ArrowRight,
  Twitter, Linkedin, Copy, Check
} from 'lucide-react';
import GoldenSpiral from '../components/GoldenSpiral';
import useConfigStore from '../store/configStore';
import { joinWaitlist } from '../services/api';
import {
  trackWaitlistView,
  trackWaitlistSubmit,
  trackWaitlistSuccess,
  trackWaitlistShare
} from '../services/analytics';

export default function Waitlist() {
  const navigate = useNavigate();
  const { goal, stack, reset } = useConfigStore();
  
  const [email, setEmail] = useState('');
  const [feedback, setFeedback] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [waitlistPosition, setWaitlistPosition] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!goal || !stack) {
      navigate('/home');
    }
  }, [goal, stack, navigate]);

  // Track waitlist view
  useEffect(() => {
    trackWaitlistView();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    setError(null);
    trackWaitlistSubmit(!!stack);

    try {
      const result = await joinWaitlist({
        email,
        sessionId: useConfigStore.getState().sessionId,
        stack,
        feedback
      });

      const position = result.position || Math.floor(Math.random() * 100) + 42;
      setWaitlistPosition(position);
      setIsSubmitted(true);
      trackWaitlistSuccess(position);
    } catch (err) {
      setError(err.message || 'Failed to join waitlist. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = () => {
    trackWaitlistShare('copy_link');
    navigator.clipboard.writeText('https://vincikit.com');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleTwitterShare = () => {
    trackWaitlistShare('twitter');
  };

  const handleLinkedInShare = () => {
    trackWaitlistShare('linkedin');
  };

  const handleStartOver = () => {
    reset();
    navigate('/home');
  };

  // Get stack data
  const stackData = stack?.stack || stack || {};
  const aiName = stackData.ai?.name || 'Claude';
  const toolCount = stackData.tools?.length || 0;
  const totalCost = stack?.totalCost || '$20-50/month';

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-gray-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          
          <h1 className="text-2xl font-bold mb-2 text-gray-900">
            You're on the list!
          </h1>
          
          <p className="text-gray-600 mb-6">
            You're #{waitlistPosition} in line. We'll email you at{' '}
            <span className="font-medium text-gray-900">{email}</span>{' '}
            when your stack is ready to deploy.
          </p>

          {/* Stack summary */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-left mb-6">
            <h3 className="font-medium text-sm text-gray-500 mb-2">Your Stack</h3>
            <p className="font-medium mb-1 text-gray-900">{aiName} + {toolCount} tools</p>
            <p className="text-sm text-gray-600">For: "{goal}"</p>
          </div>

          {/* Share */}
          <div className="mb-8">
            <p className="text-sm text-gray-500 mb-3">Share VinciKit with friends:</p>
            <div className="flex justify-center gap-3">
              <a
                href={`https://twitter.com/intent/tweet?text=Just%20configured%20my%20AI%20stack%20with%20VinciKit!%20🚀&url=https://vincikit.com`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleTwitterShare}
                className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                <Twitter className="w-5 h-5 text-gray-700" />
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=https://vincikit.com`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleLinkedInShare}
                className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                <Linkedin className="w-5 h-5 text-gray-700" />
              </a>
              <button
                onClick={handleCopyLink}
                className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                {copied ? (
                  <Check className="w-5 h-5 text-green-600" />
                ) : (
                  <Copy className="w-5 h-5 text-gray-700" />
                )}
              </button>
            </div>
          </div>

          <button
            onClick={handleStartOver}
            className="text-blue-600 hover:underline text-sm"
          >
            Configure another stack →
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className="px-6 py-4 border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto flex items-center justify-center">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 rounded flex items-center justify-center">
              <GoldenSpiral className="w-4 h-4" color="white" />
            </div>
            <span className="font-medium">VinciKit</span>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <div className="text-center mb-8">
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Mail className="w-6 h-6 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2 text-gray-900">
              Get Early Access
            </h1>
            <p className="text-gray-600">
              Your stack is configured! Join the waitlist and we'll notify you 
              when one-click deployment is ready.
            </p>
          </div>

          {/* Stack preview */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-gray-900">Your Stack</h3>
              <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                Ready to deploy
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-500">AI:</span>
                <span className="font-medium text-gray-900">{aiName}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Tools:</span>
                <span className="font-medium text-gray-900">
                  {stackData.tools?.map(t => t.name).join(', ') || 'Custom stack'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Est. cost:</span>
                <span className="font-medium text-gray-900">{totalCost}</span>
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-1">
                Anything else you'd like us to know? (optional)
              </label>
              <textarea
                id="feedback"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Tell us more about your use case..."
                rows={3}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading || !email}
              className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Joining...
                </>
              ) : (
                <>
                  Join Waitlist
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          <p className="text-xs text-gray-500 text-center mt-4">
            We'll only email you about VinciKit updates. No spam, ever.
          </p>
        </motion.div>
      </main>
    </div>
  );
}
